import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { API } from "../../App";
import { Copy, Send, Loader2 } from "lucide-react";

export const DailyDigestDialog = ({ open, onOpenChange, headers, onPosted }) => {
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");

  useEffect(() => {
    if (open) fetchDigest();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDigest = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/daily-digest`, { headers });
      setTitle(res.data.title);
      setText(res.data.text);
    } catch (error) {
      toast.error("Couldn't build today's digest");
    } finally {
      setLoading(false);
    }
  };

  const copyForWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied! Paste it into the house WhatsApp group 💬");
    } catch {
      toast.error("Couldn't copy — select the text and copy manually");
    }
  };

  const postToGuestApp = async () => {
    setPosting(true);
    try {
      await axios.post(
        `${API}/announcements`,
        { title, content: text.replace(/\*/g, ""), type: "general" },
        { headers }
      );
      toast.success("Posted to the guest app 🧡");
      onOpenChange(false);
      onPosted?.();
    } catch (error) {
      toast.error("Couldn't post the digest");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="daily-digest-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#3D4035]">☀️ Today at Savi</DialogTitle>
          <DialogDescription>
            Auto-built from today's schedule, espresso talks and rides. Tweak it, then copy or post.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[#E07A5F]" />
          </div>
        ) : (
          <>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={14}
              className="font-mono text-xs leading-relaxed bg-[#FBF7F1] border-stone-200 rounded-xl"
              data-testid="daily-digest-text"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={copyForWhatsApp}
                className="flex-1 bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full"
                data-testid="digest-copy-btn"
              >
                <Copy className="w-4 h-4 mr-2" /> Copy for WhatsApp
              </Button>
              <Button
                onClick={postToGuestApp}
                disabled={posting}
                className="flex-1 bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-full"
                data-testid="digest-post-btn"
              >
                {posting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Post to guest app
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
